import { Card, FormHelperText, Grid, TextField } from '@mui/material';
import React, { ChangeEvent, useState } from 'react';
import { Customer } from '../../models/customer.model';
import { t } from 'i18next';
import Button from '@mui/material/Button';
import { toast } from 'react-toastify';
import { NWClient } from '../../client/NWClient';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../common/constants/routes';
import { useAppSelector } from '../../common/hooks/useAppSelector';
import { selectAllCompanies } from '../../slices/companies/companiesSlice';

export const CreateCustomerProfile: React.FC = () => {
  const company = useAppSelector(selectAllCompanies)[0];
  const [formData, setFormData] = useState<Partial<Customer>>({
    company: company.id,
    customer_name: '',
    contact_name: '',
    contact_number: '',
    email_address: '',
    physical_address: '',
    postal_address: '',
    industy_type: '',
    website: '',
    customer_registration: '',
  });
  const [fieldsErrors, setFieldsErrors] = useState(null);
  const [pendingStatus, setPendingStatus] = useState(false);
  const navigate = useNavigate();

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();
    const errors: Record<string, string[]> = {};
    if (!formData.customer_name) {
      errors.customer_name = ['Customer name is required'];
    }

    if (Object.keys(errors).length > 0) {
      setFieldsErrors(errors);
      return;
    }
    setPendingStatus(true);

    NWClient.post('customer', formData, true)
      .then((res: Customer) => {
        toast.success('Customer profile created successfully!');
        setFormData({
          company: company.id,
          customer_name: '',
          contact_name: '',
          contact_number: '',
          email_address: '',
          physical_address: '',
          postal_address: '',
          industy_type: '',
          website: '',
        });
        setFieldsErrors(null);
        navigate(ROUTES.CUSTOMER_PROFILES + '/' + res.id);
      })
      .catch((error) => {
        setFieldsErrors(error.response.data);
        toast.error('A profile with the same name already exists.');
      })
      .finally(() => {
        setPendingStatus(false);
      });
  };

  return (
    <div style={{ marginTop: '-61px' }} className='profile-details-wrapper'>
      <h1 style={{ marginBottom: '1rem', textTransform: 'none' }}>{`${t(
        'pages.createCustomer'
      )}`}</h1>
      <form onSubmit={handleSubmit}>
        <Card sx={{ padding: '16px', marginBottom: '1rem' }}>
          <Grid container className='form-controls' spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={`${t('labels.customerName')} *`}
                onChange={handleInputChange}
                name='customer_name'
                value={formData.customer_name}
                error={Boolean(fieldsErrors?.customer_name)} // Add this line
                helperText={fieldsErrors?.customer_name?.join('.')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={`${t('labels.contactName')}`}
                onChange={handleInputChange}
                name='contact_name'
                value={formData.contact_name}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={`${t('labels.contactNumber')}`}
                onChange={handleInputChange}
                name='contact_number'
                value={formData.contact_number}
              />
              {Boolean(fieldsErrors?.contact_number) && (
                <FormHelperText error={true}>
                  {fieldsErrors?.contact_number.join('.')}
                </FormHelperText>
              )}
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={`${t('labels.emailAddress')}`}
                onChange={handleInputChange}
                name='email_address'
                value={formData.email_address}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={`${t('labels.customerRegistrationNumber')}`}
                onChange={handleInputChange}
                name='customer_registration'
                value={formData.customer_registration}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={`${t('labels.physicalAddress')}`}
                onChange={handleInputChange}
                name='physical_address'
                value={formData.physical_address}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={`${t('labels.postalAddress')}`}
                onChange={handleInputChange}
                name='postal_address'
                value={formData.postal_address}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={`${t('labels.industryType')}`}
                onChange={handleInputChange}
                name='industy_type'
                value={formData.industy_type}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={`${t('labels.website')}`}
                onChange={handleInputChange}
                name='website'
                value={formData.website}
              />
            </Grid>
          </Grid>
        </Card>
        <Grid container justifyContent='flex-end' gap={'8px'} mt={2}>
          <Button
            type='button'
            variant='contained'
            size='medium'
            className='btn-default btn-action-basic btn-transparent-initial btn-cancel'
            onClick={() => {
              navigate(ROUTES.CUSTOMER_PROFILES);
            }}
          >
            {`${t('buttons.goBack')}`}
          </Button>
          <Button
            type='submit'
            variant='contained'
            disabled={pendingStatus}
            className='btn-default btn-action-basic'
          >
            {pendingStatus ? 'Submitting...' : 'Submit'}
          </Button>
        </Grid>
      </form>
    </div>
  );
};
